import type { ReactNode } from "react";

import { useEmbedThemeGate } from "./useEmbedThemeGate";

interface EmbedThemeGateProps {
  /** Property slug used to fetch the published theme. */
  slug?: string;
  /**
   * Whether the SSR payload included custom theme fonts (derived from
   * `font_face_css`). When true the gate shows the spinner until those fonts
   * have downloaded; when false it renders immediately.
   */
  ssrHasCustomFonts?: boolean;
  /** Rendered while the custom theme fonts are downloading. */
  fallback: ReactNode;
  /** Rendered once the theme is applied and any custom fonts are in place. */
  children: ReactNode;
}

/**
 * Suspends rendering of `children` until the embed theme has been resolved
 * and applied to the document. While the theme is in-flight the `fallback`
 * (e.g. a spinner) is shown, so the app never paints with the wrong theme.
 *
 * Theme colours are baked into `<head>` during SSR (`+Head.tsx`) and paint
 * instantly, so the spinner only ever gates on custom fonts downloading.
 */
export function EmbedThemeGate({
  slug,
  ssrHasCustomFonts,
  fallback,
  children,
}: EmbedThemeGateProps) {
  const { isReady } = useEmbedThemeGate(slug, ssrHasCustomFonts);
  return <>{isReady ? children : fallback}</>;
}
